import React, { useEffect, useState } from 'react';
import BaseAgreementBottomSheet from '../components/bottomSheet/BaseAgreementBottomSheet';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { getJWT } from '../utils/etcUtils';
import { useColorMode } from '@chakra-ui/react';
import saveData from '../utils/saveData';
import { getAppSettings } from '../utils/NativeUtil';
import {
  getClientSocialPlatform,
  getSocialLoginMethods,
  resolveEnabledSocialLoginMethods,
  type SocialLoginMethod
} from '../data/repositories/SocialLoginMethods';

const textKeyArray = ['off', 'trade', 'mate'];

const SUPPORTED_LOCALES = ['ko', 'en', 'zh-TW', 'zh-CN', 'jp'] as const;

const normalizeLanguage = (lang?: string | null): string | null => {
  if (!lang || typeof lang !== 'string') return null;
  const base = lang.split(/[-_]/)[0]?.toLowerCase();
  if (!base) return null;
  if (base === 'ko') return 'ko';
  if (base === 'en') return 'en';
  if (base === 'zh') {
    const full = lang.toLowerCase();
    if (full.includes('tw') || full.includes('hk')) return 'zh-TW';
    return 'zh-CN';
  }
  if (base === 'ja' || base === 'jp') return 'jp';
  if (SUPPORTED_LOCALES.includes(lang as (typeof SUPPORTED_LOCALES)[number])) return lang;
  return null;
};

const primary = <span className="text-primary-light dark:text-primary-dark" />;

const noSlideVariants = {
  initial: { x: 0, opacity: 1 },
  animate: { x: 0, opacity: 1 },
  exit: { x: -300, opacity: 0 } // 여기만 슬라이드 아웃
};

const slideVariants = {
  initial: { x: 300, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: -300, opacity: 0 }
};

const sharedVariants = {
  step1: {
    x: -26,
    y: 138,
    width: 48,
    height: 75,
    rotate: 0
  },
  step2: {
    x: -105,
    y: 100,
    width: 122,
    height: 98,
    rotate: 0
  }
};

const onBoardingImageArea = [
  <div key="step0" className="w-full pt-[70px] pb-[10px] relative">
    <img
      className="absolute left-1/2 translate-x-[70px] translate-y-[-5px] mt-[30px] "
      src="/assets/onboarding/onboarding_step1_1.png"
      alt="onboarding_step1_1"
      height={'79px'}
      width={'78px'}
    />
    <img
      className="absolute left-1/2 top-1/2 translate-y-[20px] translate-x-[-50%] object-cover"
      src="/assets/onboarding/onboarding_step1_0.png"
      alt="onboarding_step1_0"
      height={'344px'}
      width={'356px'}
    />
    <img
      className="absolute left-1/2 translate-x-[-140px] translate-y-[180px] w-[78px] h-[128px] opacity-20 object-cover z-10 rotate-[-13deg]"
      src="/assets/onboarding/onboarding_all_item_cover_11.png"
      alt="onboarding_all_item_cover"
      height={'344px'}
      width={'356px'}
    />
    <motion.div
      layoutId="shared-onboarding-item"
      className="absolute left-1/2 translate-x-[-140px] translate-y-[180px] w-[78px] h-32 rounded-[10px] shadow-[1px_1px_10px_8px_rgba(0,0,0,0.13)] outline outline-8 outline-white rotate-[-13deg]"
    >
      <img
        src="/assets/onboarding/onboarding_all_item.png"
        className="object-cover object-left rounded-[10px] left-full w-[200%] h-full"
        alt="onboarding_all_item"
      />
    </motion.div>
  </div>,
  <div key="step1" className="w-full pt-[30px] pb-[10px] relative">
    <img
      className="absolute left-1/2 translate-x-[-212px] translate-y-[-10px] mt-[30px] "
      src="/assets/onboarding/onboarding_step2_1.png"
      alt="onboarding_step2_1"
      height={'135px'}
      width={'146px'}
    />
    <img
      className="absolute left-1/2 top-1/2 translate-x-[45px] translate-y-[265px]"
      src="/assets/onboarding/onboarding_step2_2.png"
      alt="onboarding_step2_2"
      height={'158px'}
      width={'130px'}
    />
    <img
      className="absolute left-1/2 top-1/2 translate-x-[-50%] mt-[100px] object-cover"
      src="/assets/onboarding/onboarding_step2_0.png"
      alt="onboarding_step2_0"
      height={'236px'}
      width={'323px'}
    />
    <motion.div
      layoutId="shared-onboarding-item"
      animate={sharedVariants.step1}
      // transition={{ duration: 0.4 }}
      className="absolute left-1/2 rounded-[3px] border border-neutral-200"
    >
      <img
        src="/assets/onboarding/onboarding_all_item_cover.png"
        className="absolute w-[48px] h-[75px] opacity-20 object-cover z-10"
        alt="onboarding_all_item_cover"
        height={'236px'}
        width={'323px'}
      />
      <img
        src="/assets/onboarding/onboarding_all_item.png"
        className="object-cover object-left rounded-[3px] left-full w-[200%] h-full"
        alt="onboarding_all_item"
      />
    </motion.div>
  </div>,
  <div key="step2" className="w-full translate-x-[15px] pt-[70px] pb-[10px] relative">
    <img
      className="absolute left-1/2 translate-x-[-195px] translate-y-[100px] mt-[30px] z-10 opacity-80"
      src="/assets/onboarding/onboarding_step3_1.png"
      alt="onboarding_step3_1"
      height={'125px'}
      width={'126px'}
    />
    <img
      className="absolute left-1/2 top-1/2 translate-x-[50px] translate-y-[10px] z-10 opacity-70"
      src="/assets/onboarding/onboarding_step3_2.png"
      alt="onboarding_step3_2"
      height={'96px'}
      width={'96px'}
    />
    <img
      className="absolute left-1/2 top-1/2 translate-x-[-50%] translate-y-[10px] object-cover"
      src="/assets/onboarding/onboarding_step3_0.png"
      alt="onboarding_step3_0"
      height={'350px'}
      width={'313px'}
    />
    <motion.div
      layoutId="shared-onboarding-item"
      animate={sharedVariants.step2}
      // transition={{ duration: 0.4 }}
      className="absolute left-1/2 rounded-[10px] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.15)]"
    >
      <img
        src="/assets/onboarding/onboarding_all_item.png"
        className="object-cover object-left rounded-[10px] left-full w-[200%] h-full"
        alt="onboarding_all_item"
      />
    </motion.div>
  </div>
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const isEnLocale = i18n.language?.startsWith('en') ?? false;
  const skipToLastStep =
    (location.state as { skipToLastStep?: boolean } | null)?.skipToLastStep ?? false;
  const [step, setStep] = useState(() => (skipToLastStep ? 2 : 0));
  const [isNext, setIsNext] = useState(skipToLastStep);
  const [prefetchedSocialMethods, setPrefetchedSocialMethods] = useState<SocialLoginMethod[]>([]);
  const [isSocialMethodsPrefetching, setIsSocialMethodsPrefetching] = useState(true);
  const [socialMethodsPrefetchError, setSocialMethodsPrefetchError] = useState<string | null>(null);
  const { colorMode } = useColorMode();

  const token = getJWT();

  useEffect(() => {
    if (token && navigate) {
      navigate('/home', { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    const applyAppSettings = async () => {
      const settings = (await getAppSettings()) as {
        language?: string;
        country?: string;
      } | null;
      if (!settings) return;

      const locale = normalizeLanguage(settings.language);
      if (locale && locale !== i18n.language) {
        saveData('i18nextLng', locale);
        i18n.changeLanguage(locale);
      }

      if (settings.country) {
        saveData('feedCountryCode', settings.country);
      }
    };
    applyAppSettings();
  }, [i18n]);

  useEffect(() => {
    if (step >= 2) {
      setTimeout(() => {
        setIsNext(true);
      }, 1000);
    }
    return () => {
      setIsNext(false);
    };
  }, [step]);

  useEffect(() => {
    let isMounted = true;
    setIsSocialMethodsPrefetching(true);
    setSocialMethodsPrefetchError(null);

    const platform = getClientSocialPlatform();
    getSocialLoginMethods({ platform })
      .then((response) => {
        if (!isMounted) return;

        const resolvedMethods = resolveEnabledSocialLoginMethods(response, platform);
        setPrefetchedSocialMethods(resolvedMethods);

        if (response.success === false && response.errorMessage) {
          setSocialMethodsPrefetchError(response.errorMessage);
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setPrefetchedSocialMethods([]);
        setSocialMethodsPrefetchError(
          t('requestVerificationCodePage.socialLoginMethodsFailedMessage')
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsSocialMethodsPrefetching(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const startLogin = (isPromotionAgreed: boolean) => {
    navigate(
      `/request-verification-code?isPromotionAgreed=${isPromotionAgreed}&isTermsAgreed=true`,
      {
        replace: true
      }
    );
  };

  const handleStep = () => {
    if (step >= 2) {
      setIsNext(true);
      return;
    }
    if (step === 0 && isEnLocale) {
      // 영어 설정 시 두 번째 스텝(step 1) 건너뛰기
      setStep(2);
    } else {
      setStep(step + 1);
    }
  };

  const handleAgreement = (isChild: boolean, isPromotionAgreed: boolean) => {
    if (isChild) {
      // setIsOpenAlert(true);
    } else {
      startLogin(isPromotionAgreed);
    }
  };

  const onBoardingBackGroundArea = [
    <React.Fragment key="bg0">
      <div className="absolute left-0 top-[280px] z-0 w-52 h-52">
        <img
          src={`/assets/onboarding/bg_onboarding_step1_0_${colorMode}.png`}
          alt="bg_onboarding_step1_0"
        />
      </div>
      <div className="absolute right-0 top-[50px] z-0 w-[154px] h-[154px]">
        <img
          src={`/assets/onboarding/bg_onboarding_step1_1_${colorMode}.png`}
          alt="bg_onboarding_step1_1"
        />
      </div>
    </React.Fragment>,
    <React.Fragment key="bg1">
      <div className="absolute left-0 top-[480px] z-0 w-80 h-80">
        <img
          src={`/assets/onboarding/bg_onboarding_step2_0_${colorMode}.png`}
          alt="bg_onboarding_step2_0"
        />
      </div>
    </React.Fragment>,
    <React.Fragment key="bg2">
      <div className="absolute left-0 top-[350px] z-0 w-44 h-44">
        <img
          src={`/assets/onboarding/bg_onboarding_step3_0_${colorMode}.png`}
          alt="bg_onboarding_step3_0"
        />
      </div>
      <div className="absolute right-0 top-[220px] z-0 w-44 h-44">
        <img
          src={`/assets/onboarding/bg_onboarding_step3_1_${colorMode}.png`}
          alt="bg_onboarding_step3_1"
        />
      </div>
    </React.Fragment>
  ];

  if (token) {
    return <div />;
  }

  return (
    <div className="relative overflow-hidden mx-auto max-w-xl h-[calc(var(--vh,1vh)*100)] pt-[env(safe-area-inset-top)]">
      {/* 건너뛰기 버튼 */}
      {step === 2 ? null : (
        <div className="absolute flex top-[10px] z-[50] right-[16px] mt-[env(safe-area-inset-top)]">
          <button onClick={() => setIsNext(true)} type="button">
            <span className="text-main-light-3 text-[15px] font-normal">
              {t('OnBoardingPage.skip')}
            </span>
          </button>
        </div>
      )}
      {onBoardingBackGroundArea[step]}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          custom={step}
          variants={step === 0 ? noSlideVariants : slideVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.3 }}
          className="relative z-10"
        >
          {/* 배경 영역 */}
          {/* 텍스트 영역 */}
          <div className="mx-[12px] mt-[68px] mb-[6px] z-[50]">
            <div className="flex flex-col justify-center items-center mb-[12px]">
              <span className="text-center text-text-main-lv1 leading-[30px] text-[24px] font-bold whitespace-pre-line">
                <Trans
                  i18nKey={`OnBoardingPage.${textKeyArray[step]}Title`}
                  components={{ primary }}
                />
              </span>
            </div>
            <div className="flex flex-col justify-center items-center">
              <span className="text-center text-main-light-4 whitespace-pre-line dark:text-main-dark-4 text-[15px] font-medium leading-tight">
                <Trans i18nKey={`OnBoardingPage.${textKeyArray[step]}Description`} />
              </span>
            </div>
          </div>
          {/* 이미지 영역 */}
          {onBoardingImageArea[step]}
        </motion.div>
      </AnimatePresence>
      {/* step 표시 영역 */}
      <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+8px)] flex flex-col justify-center items-center w-full gap-[20px]">
        <div className="flex justify-center items-center flex-row gap-2 mt-[10px] ">
          {(isEnLocale ? [0, 2] : [0, 1, 2]).map((stepIdx) => (
            <div
              key={stepIdx}
              className={`
                w-2 h-2 rounded-full 
                ${step === stepIdx ? 'bg-neutral-700' : 'bg-zinc-300'}
              `}
            />
          ))}
        </div>
        {/* 다음 버튼 */}
        <button
          className="flex justify-center items-center px-6 py-3.5 w-[90%] rounded-full bg-main-light-2"
          type="button"
          onClick={handleStep}
        >
          <span className="text-pure-white text-[16px] font-semibold">
            {t('OnBoardingPage.next')}
          </span>
        </button>
      </div>

      {isNext && (
        <BaseAgreementBottomSheet
          isOpen={true}
          onAgreementClick={(isChild: boolean, isPromotionAgreed: boolean) =>
            handleAgreement(isChild, isPromotionAgreed)
          }
          title={false}
          description={false}
          transparentBackground={true}
          showLoginMethods={true}
          prefetchedSocialMethods={prefetchedSocialMethods}
          isSocialMethodsPrefetching={isSocialMethodsPrefetching}
          socialMethodsPrefetchError={socialMethodsPrefetchError || undefined}
        />
      )}
    </div>
  );
}
